/**
 * Map 1 km markers along an activity (distance from start in meters = km * 1000)
 * to lap rows, exercise preview segments, and Strava metric/mile splits using cumulative distance.
 */

/**
 * @param {Array<{ lap_index?: number, distance?: number|string, id?: number }>} laps
 * @returns {{ lapIndex: number, startM: number, endM: number, rowKey: string }[]}
 */
export function buildLapRanges(laps) {
  if (!Array.isArray(laps) || laps.length === 0) return [];
  const sorted = [...laps].sort((a, b) => (Number(a.lap_index) || 0) - (Number(b.lap_index) || 0));
  let cum = 0;
  return sorted.map((lap) => {
    const d = Math.max(0, parseFloat(lap.distance) || 0);
    const startM = cum;
    cum += d;
    const lapIndex = Number(lap.lap_index);
    return {
      lapIndex: Number.isFinite(lapIndex) ? lapIndex : sorted.indexOf(lap),
      startM,
      endM: cum,
      rowKey: lap.id != null ? `id:${lap.id}` : `lap:${lap.lap_index}`,
    };
  });
}

/**
 * @param {Array<{ distance_m?: number, duration_sec?: number, split_measure_mode?: string }>} splits
 * @param {number} activityDistanceM — fallback total for duration-only segments
 */
export function buildExerciseSplitRanges(splits, activityDistanceM) {
  if (!Array.isArray(splits) || splits.length === 0) return [];
  const totalDur = splits.reduce((a, s) => a + (Number(s.duration_sec) || 0), 0);
  const totalDist = Math.max(0, Number(activityDistanceM) || 0);

  let cum = 0;
  return splits.map((s, i) => {
    let d = Number(s.distance_m);
    if (!Number.isFinite(d) || d <= 0) {
      const dur = Number(s.duration_sec) || 0;
      if (totalDur > 0 && totalDist > 0 && dur > 0) {
        d = (dur / totalDur) * totalDist;
      } else {
        d = 0;
      }
    }
    const startM = cum;
    cum += Math.max(0, d);
    return { index: i, startM, endM: cum };
  });
}

/**
 * Strava splits ordered by split_index; uses each row's distance (m) when present else 1000 (metric) / 1609.34 (standard).
 */
export function buildStravaSplitRanges(splits, splitUnit) {
  if (!Array.isArray(splits) || splits.length === 0) return [];
  const sorted = [...splits].sort((a, b) => (Number(a.split_index) || 0) - (Number(b.split_index) || 0));
  const defaultChunk = splitUnit === 'standard' ? 1609.344 : 1000;
  let cum = 0;
  return sorted.map((s) => {
    const raw = parseFloat(s.distance);
    const d = Number.isFinite(raw) && raw > 0 ? raw : defaultChunk;
    const startM = cum;
    cum += d;
    return {
      splitIndex: Number(s.split_index),
      startM,
      endM: cum,
    };
  });
}

/** @param {number} km — integer 1..N (marker label) */
export function positionMForKm(km) {
  return km * 1000;
}

/**
 * Point at km marker lies in [startM, endM] with start exclusive so lap boundaries stay unambiguous:
 * (startM, endM] in meters.
 */
export function rangeIndexForPositionM(positionM, ranges) {
  if (positionM == null || !Number.isFinite(positionM) || !ranges?.length) return null;
  const hit = ranges.find((r) => positionM > r.startM && positionM <= r.endM);
  return hit ?? null;
}

export function lapForKm(km, lapRanges) {
  const r = rangeIndexForPositionM(positionMForKm(km), lapRanges);
  return r ? r.lapIndex : null;
}

export function exerciseIndexForKm(km, exerciseRanges) {
  const r = rangeIndexForPositionM(positionMForKm(km), exerciseRanges);
  return r ? r.index : null;
}

export function stravaSplitForKm(km, splitRanges) {
  const r = rangeIndexForPositionM(positionMForKm(km), splitRanges);
  return r ? r.splitIndex : null;
}

/** All integer km markers with position in (startM, endM] */
export function kmMarkersInRange(startM, endM) {
  const out = [];
  const lo = Math.floor(startM / 1000);
  const hi = Math.ceil(endM / 1000);
  for (let k = lo + 1; k <= hi; k++) {
    const p = k * 1000;
    if (p > startM && p <= endM) out.push(k);
  }
  return out;
}

export function kmMarkersForLap(lapIndex, lapRanges) {
  const r = lapRanges.find((x) => x.lapIndex === lapIndex);
  if (!r) return [];
  return kmMarkersInRange(r.startM, r.endM);
}

export function kmMarkersForExerciseIndex(splitIndex, exerciseRanges) {
  const r = exerciseRanges.find((x) => x.index === splitIndex);
  if (!r) return [];
  return kmMarkersInRange(r.startM, r.endM);
}

export function kmMarkersForStravaSplit(splitIndex, splitRanges) {
  const r = splitRanges.find((x) => x.splitIndex === splitIndex);
  if (!r) return [];
  return kmMarkersInRange(r.startM, r.endM);
}

export function lapForExerciseIndex(splitIndex, exerciseRanges, lapRanges) {
  const r = exerciseRanges.find((x) => x.index === splitIndex);
  if (!r || !lapRanges.length) return null;
  const mid = (r.startM + r.endM) / 2;
  const hit = rangeIndexForPositionM(mid, lapRanges);
  return hit ? hit.lapIndex : null;
}

export function exerciseIndexForLap(lapIndex, lapRanges, exerciseRanges) {
  const r = lapRanges.find((x) => x.lapIndex === lapIndex);
  if (!r || !exerciseRanges.length) return null;
  const mid = (r.startM + r.endM) / 2;
  const hit = rangeIndexForPositionM(mid, exerciseRanges);
  return hit ? hit.index : null;
}
